/*
 * /zx-tape — лента ZX Spectrum ↔ звук.
 *
 * Три вкладки, три задачи одного человека: загрузить игру в живой Spectrum с
 * телефона, оцифровать свою кассету и сделать ленту из собственной программы.
 */

import { parseTape, parseHeader, writeTap, writeTzx, checksumOk, TSTATES_48K, TSTATES_128K } from './tzx.js';
import { buildPulses, describeBlock } from './pulses.js';
import { wavChunks, sampleCount } from './wav.js';
import { screenToRGBA, detokenize, fileLabel } from './spectrum.js';
import { TapePlayer } from './player.js';
import { initDecodeTab } from './decode-ui.js';
import { initMakeTab } from './make-ui.js';

const T = (k, sub) => {
    let s = (window.__ZXT_T && window.__ZXT_T[k]) || k;
    /* ⚠️ Подставляем от ДЛИННЫХ имён к коротким. Иначе «:n» съедает начало
       «:name», и «Продолжить с блока :n — :name» выходит как «…— 2ame».
       Поймано тестом закладки; ловушка общая для любой пары вида :n/:name. */
    if (sub) {
        for (const p of Object.keys(sub).sort((a, b) => b.length - a.length)) {
            s = s.split(':' + p).join(sub[p]);
        }
    }
    return s;
};
const $ = (sel, root) => (root || document).querySelector(sel);
const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));
const fmtTime = (sec) => {
    if (!isFinite(sec)) return '—';
    /* Округлять ОСТАТОК нельзя: 59.6 с превращается в «0:60». Округляем целое
       число секунд и уже его разбираем на минуты. */
    const total = Math.round(sec);
    return Math.floor(total / 60) + ':' + String(total % 60).padStart(2, '0');
};
const fmtSize = (n) => n >= 1024 ? (n / 1024).toFixed(1) + ' KB' : n + ' B';
const stage = (n) => { try { window.__stage && window.__stage(n); } catch { } };
const funnel = (n, d) => { try { window.__aeFunnel && window.__aeFunnel(n, d || {}); } catch { } };

export const state = {
    tape: null,
    fileName: '',
    pulses: null,
    index: null,
    player: null,
    settings: {
        rate: 44100,
        bits: 8,
        amplitude: 0.9,
        invert: false,
        machine: '48',
        stopAtMarks: true,
        leadInMs: 500,
    },
};

/* ───────────────────────── лента → звук ───────────────────────── */

function clockOf() {
    return state.settings.machine === '128' ? TSTATES_128K : TSTATES_48K;
}

function rebuildPulses() {
    /* Настройки применяются и ДО открытия ленты (их восстанавливает
       __toolSettings при первой отрисовке). Без этой проверки первый же
       apply() падал на null.blocks — и, что важнее, ронял boot() целиком:
       вкладки «Оцифровать» и «Сделать ленту» не инициализировались вовсе и
       выглядели просто мёртвыми. */
    if (!state.tape) return null;
    const built = buildPulses(state.tape.blocks, {
        clock: clockOf(),
        machine48: state.settings.machine === '48',
    });
    state.pulses = built.pulses;
    state.index = built.index;
    state.seconds = built.seconds;
    state.warnings = built.warnings;
    /* ⚠️ Импульсы сменились — то, что лежит в проигрывателе, устарело.
       Без этого флага кнопка «играть» после загрузки ВТОРОЙ ленты
       продолжала играть ПЕРВУЮ: ветка «возобновить» не перезагружает
       данные, а состояние проигрывателя после паузы не «idle». Поймано
       проверкой капа: счётчик не убывал, потому что вторая лента вообще
       не игралась. */
    state.needsLoad = true;
    return built;
}

/** Найти загрузочный экран: блок CODE на 16384 длиной 6912 и его данные. */
function findScreen(blocks) {
    for (let i = 0; i < blocks.length; i++) {
        const h = parseHeader(blocks[i].data);
        if (h && h.kind === 'screen') {
            for (let j = i + 1; j < Math.min(blocks.length, i + 4); j++) {
                const d = blocks[j].data;
                if (d && d.length >= 6913 && d[0] === 0xff) return d.subarray(1, 6913);
            }
        }
    }
    /* Экран без заголовка (турбо-загрузчики так и делают): ищем блок ровно на
       6912 полезных байт. */
    for (const b of blocks) {
        if (b.data && (b.data.length === 6914 || b.data.length === 6912)) {
            return b.data.length === 6912 ? b.data : b.data.subarray(1, 6913);
        }
    }
    return null;
}

function drawScreen(scr) {
    const wrap = $('.zxt-screen');
    const canvas = $('#zxtScreen');
    if (!wrap || !canvas) return;
    if (!scr) { wrap.style.display = 'none'; return; }
    wrap.style.display = '';
    const ctx = canvas.getContext('2d');
    const draw = (phase) => {
        const rgba = screenToRGBA(scr, { flashPhase: phase });
        ctx.putImageData(new ImageData(rgba, 256, 192), 0, 0);
    };
    draw(0);
    /* FLASH — часть картинки, а не украшение: половина загрузочных экранов
       мигает надписью «press any key», и без мигания скриншот выглядит
       сломанным. */
    clearInterval(state.flashTimer);
    let phase = 0;
    state.flashTimer = setInterval(() => { phase ^= 1; draw(phase); }, 640);
}

function tapeTitle() {
    for (const b of state.tape.blocks) {
        if (b.id === 0x32 && b.info) {
            const t = b.info.find(x => x.kind === 0);
            if (t) return t.text;
        }
    }
    for (const b of state.tape.blocks) {
        const h = parseHeader(b.data);
        if (h && h.name) return h.name;
    }
    return state.fileName.replace(/\.[^.]+$/, '');
}

function renderBlocks() {
    const list = $('.zxt-blocks');
    if (!list) return;
    const rows = [];
    const idx = state.index || [];
    const byBlock = new Map();
    for (const e of idx) if (!byBlock.has(e.block)) byBlock.set(e.block, e);
    state.tape.blocks.forEach((b, i) => {
        const d = describeBlock(b, i);
        const e = byBlock.get(i);
        if (d.kind === 'group_end') return;
        let icon = {
            header: '📇', data: '📦', tone: '〰️', pulses: '⚡', pause: '⏸', stop: '⏹', stop48: '⏹',
            group_start: '📁', text: '💬', message: '💬', archive: 'ℹ️', direct: '🎚️', generalized: '🧩',
            csw: '🗜', loop_start: '🔁', loop_end: '🔁', select: '📋', jump: '↪️', unknown: '❓',
        }[d.kind] || '▫️';
        let title = d.title || '';
        let sub = '';
        if (d.kind === 'header' && d.header) {
            const lbl = fileLabel(d.header);
            title = d.header.name || T('unnamed');
            sub = T('hdr_' + d.header.kind) + (d.header.kind === 'code' ? ' @' + d.header.start : '')
                + ' · ' + fmtSize(d.header.length)
                + (d.header.autostart != null ? ' · LINE ' + d.header.autostart : '');
            /* Значок ТИПА ФАЙЛА вместо общего «заголовок»: две картинки подряд
               (📇 📄 HELLO) не говорят ничего сверх одной. */
            if (lbl) icon = lbl.icon;
        } else if (d.kind === 'data') {
            title = T('block_data');
            sub = fmtSize(d.bytes) + (d.turbo ? ' · ' + T('turbo') : '') + (d.pure ? ' · ' + T('pure_data') : '');
            if (d.timings && d.timings.zero) sub += ' · 0=' + d.timings.zero + ' 1=' + d.timings.one;
        } else if (d.kind === 'tone') {
            title = T('block_tone'); sub = d.count + ' × ' + d.pulse + ' T';
        } else if (d.kind === 'pause') {
            title = T('block_pause'); sub = (d.pause / 1000).toFixed(1) + ' ' + T('sec_short');
        } else if (d.kind === 'stop' || d.kind === 'stop48') {
            title = d.kind === 'stop' ? T('block_stop') : T('block_stop48');
        } else if (d.kind === 'group_start') {
            title = '📁 ' + (d.title || T('block_group'));
        } else if (d.kind === 'archive') {
            title = T('block_archive');
            sub = (d.info || []).map(x => x.text).join(' · ').slice(0, 120);
        } else if (d.kind === 'text' || d.kind === 'message') {
            title = '💬 ' + (d.title || '');
        } else if (!title) {
            title = T('block_' + d.kind) !== 'block_' + d.kind ? T('block_' + d.kind) : d.kind;
        }
        const cs = d.checksum === false ? `<span class="zxt-bad" title="${T('checksum_bad')}">✕</span>`
            : d.checksum === true ? `<span class="zxt-ok" title="${T('checksum_ok')}">✓</span>` : '';
        rows.push(`<div class="zxt-row" data-block="${i}">
            <span class="zxt-row-i">${i}</span>
            <span class="zxt-row-icon">${icon}</span>
            <span class="zxt-row-main"><b>${escapeHtml(title)}</b>${sub ? '<span class="zxt-row-sub">' + escapeHtml(sub) + '</span>' : ''}</span>
            <span class="zxt-row-time">${e && e.seconds > 0.05 ? fmtTime(e.seconds) : ''}</span>
            ${cs}
            <button class="lg-btn zxt-row-play" data-play="${i}" title="${T('play_from_here')}">▶</button>
        </div>`);
    });
    list.innerHTML = rows.join('');
}

function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function updateSummary() {
    const el = $('.zxt-summary');
    if (!el) return;
    const files = state.tape.blocks.map(b => parseHeader(b.data)).filter(Boolean);
    const turbo = state.tape.blocks.filter(b => b.id === 0x11 || b.id === 0x14).length;
    const stops = (state.index || []).filter(e => e.stop).length;
    const parts = [
        `<b>${escapeHtml(tapeTitle())}</b>`,
        T('summary_format', { fmt: state.tape.format.toUpperCase() }),
        T('summary_blocks', { n: state.tape.blocks.length }),
        T('summary_time', { time: fmtTime(state.seconds) }),
    ];
    if (files.length) parts.push(T('summary_files', { n: files.length }));
    if (turbo) parts.push(T('summary_turbo', { n: turbo }));
    if (stops) parts.push(T('summary_stops', { n: stops }));
    el.innerHTML = parts.join(' · ');

    /* Диагноз ленты. Пустой файл и обрезанный разбирались «успешно» и
       показывали «блоков: 0» без единого слова о том, что это ненормально —
       человек оставался гадать, он ли что-то сделал не так. */
    /* Вердикт одной строкой. Список блоков с галочками есть, но человек
       перед загрузкой в живую машину хочет знать ОДНО: пойдёт или нет. */
    const romBlocks = state.tape.blocks.filter(b => b.id === 0x10 || b.id === 0x11);
    const checked = romBlocks.map(b => checksumOk(b.data)).filter(v => v !== null);
    const bad = checked.filter(v => v === false).length;
    const verdict = document.createElement('div');
    verdict.className = 'zxt-verdict ' + (bad ? 'zxt-verdict-warn' : 'zxt-verdict-ok');
    if (!state.tape.blocks.length) verdict.textContent = '';
    else if (bad) verdict.textContent = T('verdict_bad', { bad, total: checked.length });
    else if (checked.length) verdict.textContent = T('verdict_ok', { n: checked.length, time: fmtTime(state.seconds) });
    else verdict.textContent = T('verdict_unknown', { time: fmtTime(state.seconds) });
    if (verdict.textContent) el.appendChild(verdict);

    const warns = [];
    if (!state.tape.blocks.length) warns.push(T('warn_empty_tape'));
    const cut = state.tape.blocks.filter(b => b.truncated || b.fatal).length;
    if (cut) warns.push(T('warn_truncated', { n: cut }));
    const mismatch = headerLengthMismatch(state.tape.blocks);
    if (mismatch) warns.push(T('warn_length_mismatch', { name: mismatch.name, said: mismatch.said, real: mismatch.real }));
    if (warns.length) {
        const w = document.createElement('div');
        w.className = 'zxt-tape-warn';
        w.textContent = warns.join(' · ');
        el.appendChild(w);
        funnel('tape_warn', { empty: !state.tape.blocks.length ? 1 : 0, cut, mismatch: mismatch ? 1 : 0 });
    }
}

/**
 * Заголовок обещает N байт, а в блоке данных их меньше — на живой машине это
 * и есть «R Tape loading error». Ловится за один проход и стоит того: у
 * архивиста это первый вопрос к найденной кассете.
 */
function headerLengthMismatch(blocks) {
    for (let i = 0; i < blocks.length; i++) {
        const h = parseHeader(blocks[i].data);
        if (!h) continue;
        const next = blocks[i + 1];
        if (!next || !next.data || next.data.length < 2 || next.data[0] !== 0xff) continue;
        const real = next.data.length - 2;
        if (real !== h.length) return { name: h.name || '?', said: h.length, real };
    }
    return null;
}

function updateProgress(pos) {
    const bar = $('.zxt-bar-fill');
    const label = $('.zxt-pos');
    if (!state.pulses) return;
    const clock = clockOf();
    const total = state.seconds || 1;
    const played = pos ? pos.t / clock : 0;
    if (bar) bar.style.width = Math.min(100, 100 * played / total) + '%';
    if (label) label.textContent = fmtTime(played) + ' / ' + fmtTime(total);
    if (pos) {
        const cur = (state.index || []).find(e => pos.index >= e.pulseStart && pos.index < e.pulseEnd);
        $$('.zxt-row').forEach(r => r.classList.toggle('zxt-row-now', cur && +r.dataset.block === cur.block));
        if (cur && cur.block !== state.lastMarked) { state.lastMarked = cur.block; saveMark(cur.block); }
    }
}

async function ensurePlayer() {
    if (!state.player) {
        state.player = new TapePlayer({
            workletUrl: (window.__ZXT && window.__ZXT.workletUrl) || '/js/zx-tape/tape-worklet.js',
            clock: clockOf(),
            onPos: updateProgress,
            onState: (st) => {
                $('.zxt-play').textContent = st === 'playing' ? '⏸' : '▶';
                if (st === 'stopped') {
                    window.__toast && window.__toast(T('stopped_at_mark'), 'info');
                    funnel('stop_mark', {});
                }
                if (st === 'ended') funnel('play_end', {});
            },
        });
    }
    return state.player;
}

/**
 * Признак «не грузится»: сколько раз запускали ОДНУ ленту и трогали ли после
 * этого громкость с полярностью. Без этого числа воронка отвечает только на
 * «дошёл ли до кнопки», а не на «получилось ли».
 */
function trackStruggle(what, extra) {
    state.plays = state.plays || 0;
    if (what === 'play') state.plays++;
    if (what === 'setting' && state.plays > 0) {
        state.tweaksAfterPlay = (state.tweaksAfterPlay || 0) + 1;
        funnel('tweak_after_play', { key: extra, plays: state.plays, tweaks: state.tweaksAfterPlay });
    }
}

function nextStopIndex(fromPulse) {
    if (!state.settings.stopAtMarks) return -1;
    for (const e of state.index || []) {
        if (e.stop && e.pulseStart > fromPulse) return e.pulseStart;
    }
    return -1;
}

async function playFrom(blockIndex) {
    if (!checkAudioSupport()) return;
    if (!spend()) return;
    const player = await ensurePlayer();
    const entry = (state.index || []).find(e => e.block === blockIndex);
    const from = entry ? entry.pulseStart : 0;
    player.clock = clockOf();
    await player.load(state.pulses, {
        clock: clockOf(),
        amplitude: state.settings.amplitude,
        invert: state.settings.invert,
    });
    state.needsLoad = false;
    player.play(from, nextStopIndex(from));
    trackStruggle('play');
    funnel('play', {
        block: blockIndex, machine: state.settings.machine,
        /* Который это запуск ОДНОЙ И ТОЙ ЖЕ ленты: 1 — норма, 4+ — человек
           воюет с громкостью, и это надо видеть в воронке. */
        attempt: state.plays,
        invert: state.settings.invert ? 1 : 0,
        amp: state.settings.amplitude,
        mode: state.player && state.player.mode,
    });
}

/* ───────────────────────── закладка на ленте ─────────────────────────
 *
 * Многозагрузочная игра — это сорок минут ленты и десяток остановок: уровень
 * загрузился, человек играет час, возвращается — и начинает С НАЧАЛА, потому
 * что страница ничего не помнит. Запоминаем последний сыгранный блок для
 * КАЖДОЙ ленты отдельно и предлагаем продолжить. Неделя жизни: позже это уже
 * не «продолжить», а чужой мусор.
 */
const MARK_TTL = 7 * 24 * 3600 * 1000;

function markStore() {
    try { return JSON.parse(localStorage.getItem('zxtape:marks') || '{}'); }
    catch { return {}; }
}

function markKey() {
    const k = tapeKey();
    return k && k.length ? String(k[0]) : (state.fileName ? 'name:' + state.fileName : null);
}

function saveMark(blockIndex) {
    const key = markKey();
    if (!key || !blockIndex) return;
    try {
        const all = markStore();
        all[key] = { block: blockIndex, at: Date.now(), name: state.fileName };
        /* Чистим старое здесь же: иначе хранилище растёт вечно, а «свежесть»
           закладки — часть её смысла. */
        for (const k2 of Object.keys(all)) if (Date.now() - (all[k2].at || 0) > MARK_TTL) delete all[k2];
        localStorage.setItem('zxtape:marks', JSON.stringify(all));
    } catch { }
}

function loadMark() {
    const key = markKey();
    if (!key) return null;
    const rec = markStore()[key];
    if (!rec || Date.now() - (rec.at || 0) > MARK_TTL) return null;
    if (!state.tape || rec.block >= state.tape.blocks.length) return null;
    return rec;
}

function renderMark() {
    const box = $('.zxt-mark');
    if (!box) return;
    const rec = loadMark();
    if (!rec) { box.style.display = 'none'; box.innerHTML = ''; return; }
    const d = describeBlock(state.tape.blocks[rec.block], rec.block);
    const label = (d.title || T('block_' + d.kind) || '').slice(0, 40);
    box.style.display = 'block';
    box.innerHTML = '';
    const btn = document.createElement('button');
    btn.className = 'lg-btn lg-btn-ghost lg-btn-sm';
    btn.textContent = T('resume_from', { n: rec.block, name: label });
    btn.addEventListener('click', () => { playFrom(rec.block); funnel('resume', { block: rec.block }); });
    box.appendChild(btn);
}

/* ───────────────────────── дневной кап ─────────────────────────
 *
 * Единица — ЛЕНТА, а не нажатие. Работа здесь итеративная: сыграл в живую
 * машину, поймал «R Tape loading error», поменял громкость или полярность,
 * сыграл снова — и это по-прежнему ОДНА лента. Поэтому ключ считается от
 * файла: повтор той же ленты в тот же день бесплатен, а слот тратит только
 * ВЫДАННЫЙ результат (первое воспроизведение или первая выгрузка).
 */

/** Ключ ленты для счётчика: сам файл, если он есть; иначе — имя. */
function tapeKey() {
    try {
        if (state.file && window.__toolLimit && window.__toolLimit.fileKey) {
            const k = window.__toolLimit.fileKey(state.file);
            if (k) return [k];
        }
    } catch { }
    return state.fileName ? ['name:' + state.fileName] : null;
}

/**
 * Списать слот за выданный результат. Возвращает false, если день исчерпан
 * (стена уже показана примитивом) — вызывающий просто ничего не делает.
 */
function spend(keys) {
    if (state.spent) return true;                 // за эту ленту уже заплачено
    if (!window.__toolLimit || !window.__toolLimit.begin) return true;
    const run = window.__toolLimit.begin('zx-tape', keys === undefined ? tapeKey() : keys);
    if (!run) { funnel('cap_wall', {}); return false; }
    run.commit();
    state.spent = true;
    return true;
}

/* ───────────────────────── выгрузка ───────────────────────── */

function baseName() {
    const raw = state.fileName.replace(/\.[^.]+$/, '') || 'tape';
    try {
        return window.__outputName ? window.__outputName.sanitize(raw) : raw.replace(/[\\/:*?"<>|]/g, '_');
    } catch { return raw; }
}

function downloadWav() {
    if (!spend()) return;
    const chunks = [];
    const opts = {
        clock: clockOf(),
        rate: state.settings.rate,
        bits: state.settings.bits,
        amplitude: state.settings.amplitude,
        invert: state.settings.invert,
        leadInMs: state.settings.leadInMs,
        tailMs: 0,
    };
    for (const c of wavChunks(state.pulses, opts)) chunks.push(c);
    const blob = new Blob(chunks, { type: 'audio/wav' });
    window.__downloadFile(blob, baseName() + '.wav');
    funnel('download_wav', { rate: opts.rate, bits: opts.bits, mb: Math.round(blob.size / 1048576) });
    try {
        window.__aeToolComplete && window.__aeToolComplete({ ok: 1, kind: 'wav', bytes: blob.size });
    } catch { }
}

function downloadTape(format) {
    if (!spend()) return;
    let bytes, name;
    if (format === 'tap') {
        const r = writeTap(state.tape.blocks);
        bytes = r.bytes; name = baseName() + '.tap';
        if (r.dropped) window.__toast(T('tap_dropped', { n: r.dropped }), 'warning');
    } else {
        bytes = writeTzx(state.tape.blocks);
        name = baseName() + '.tzx';
    }
    window.__downloadFile(new Blob([bytes], { type: 'application/octet-stream' }), name);
    funnel('download_tape', { format });
}

/** Вынуть содержимое: экран в PNG, программу в текст, код в bin. */
function extractFiles() {
    if (!spend()) return;
    const out = [];
    const blocks = state.tape.blocks;
    for (let i = 0; i < blocks.length; i++) {
        const h = parseHeader(blocks[i].data);
        if (!h) continue;
        let payload = null;
        for (let j = i + 1; j < Math.min(blocks.length, i + 4); j++) {
            const d = blocks[j].data;
            if (d && d.length > 2 && d[0] === 0xff) { payload = d.subarray(1, d.length - 1); break; }
        }
        if (!payload) continue;
        const nm = (h.name || 'file').replace(/[\\/:*?"<>|]/g, '_').trim() || 'file';
        if (h.kind === 'screen') {
            const cv = document.createElement('canvas');
            cv.width = 256; cv.height = 192;
            cv.getContext('2d').putImageData(new ImageData(screenToRGBA(payload), 256, 192), 0, 0);
            out.push({ name: nm + '.png', canvas: cv });
        } else if (h.kind === 'program') {
            const lines = detokenize(payload, h.varStart);
            const text = lines.map(l => l.line + ' ' + l.text).join('\n');
            out.push({ name: nm + '.bas.txt', blob: new Blob([text], { type: 'text/plain;charset=utf-8' }) });
        } else {
            out.push({ name: nm + '.bin', blob: new Blob([payload], { type: 'application/octet-stream' }) });
        }
    }
    if (!out.length) { window.__toast(T('nothing_to_extract'), 'warning'); return; }
    const box = $('.zxt-extract');
    box.style.display = 'block';          // скрыт правилом CSS — см. make-ui.js
    box.innerHTML = '<div class="zxt-extract-title">' + T('extracted') + '</div>';

    /* Один архив вместо кнопки на каждый файл: браузер отдаёт ровно ОДИН файл
       за нажатие, и на многозагрузочной ленте это превращается в десяток
       нажатий подряд. Кнопки ниже остаются — иногда нужен ровно один файл. */
    if (out.length > 1) {
        const zipBtn = document.createElement('button');
        zipBtn.className = 'lg-btn zxt-zip-all';
        zipBtn.textContent = T('save_all_zip', { n: out.length });
        zipBtn.addEventListener('click', async () => {
            const entries = [];
            for (const item of out) {
                const blob = item.canvas ? await new Promise(res => item.canvas.toBlob(res, 'image/png')) : item.blob;
                entries.push({ name: item.name, blob });
            }
            /* Через общий примитив, а не свой писатель ZIP: у многозагрузочной
               ленты блоки кода часто зовутся одинаково (или никак — «file»), и
               архив нёс несколько «GAME.bin», которые при распаковке затирали
               друг друга. Примитив разводит имена: GAME.bin, GAME (2).bin. */
            const BD = window.__batchDeliver || await window.__ensureBatchDeliver();
            const res = await BD.deliver(entries, { slug: 'zx-tape', zipName: baseName() + '.zip', archiveOnly: true });
            if (res && res.via === 'zip') funnel('extract_zip', { n: entries.length });
        });
        box.appendChild(zipBtn);
    }
    out.forEach(item => {
        const row = document.createElement('div');
        row.className = 'zxt-row';
        row.innerHTML = `<span class="zxt-row-main"><b>${escapeHtml(item.name)}</b></span>`;
        const btn = document.createElement('button');
        btn.className = 'lg-btn';
        btn.textContent = T('save');
        btn.addEventListener('click', () => {
            if (item.canvas) item.canvas.toBlob(b => window.__downloadFile(b, item.name), 'image/png');
            else window.__downloadFile(item.blob, item.name);
            funnel('extract_save', { kind: item.name.split('.').pop() });
        });
        row.appendChild(btn);
        box.appendChild(row);
    });
    funnel('extract', { n: out.length });
}

/** Показать листинг Бейсика прямо на странице. */
function showListing() {
    const blocks = state.tape.blocks;
    for (let i = 0; i < blocks.length; i++) {
        const h = parseHeader(blocks[i].data);
        if (h && h.kind === 'program') {
            for (let j = i + 1; j < Math.min(blocks.length, i + 4); j++) {
                const d = blocks[j].data;
                if (d && d.length > 2 && d[0] === 0xff) {
                    const lines = detokenize(d.subarray(1, d.length - 1), h.varStart);
                    const box = $('.zxt-listing');
                    box.style.display = 'block';   // скрыт правилом CSS, не инлайном
                    box.textContent = lines.map(l => l.line + ' ' + l.text).join('\n') || T('listing_empty');
                    funnel('listing', { lines: lines.length });
                    return;
                }
            }
        }
    }
    window.__toast(T('no_basic'), 'warning');
}

/* ───────────────────────── загрузка файла ───────────────────────── */

async function onFile(file) {
    try {
        stage('file_selected');
        const buf = new Uint8Array(await file.arrayBuffer());
        let bytes = buf, name = file.name;
        if (/\.zip$/i.test(file.name) || (buf[0] === 0x50 && buf[1] === 0x4b)) {
            const inner = await unzipFirstTape(buf);
            /* Своё сообщение отдаём тостом напрямую: __handleError клеит
               спереди служебный контекст («zx-tape: …»), и человек читает
               наш текст с технической приставкой. */
            if (!inner) { window.__toast(T('zip_no_tape'), 'error'); funnel('open_failed', { code: 'zip_no_tape' }); return; }
            bytes = inner.bytes; name = inner.name;
        }
        if (state.player && state.player.state === 'playing') state.player.pause();
        state.tape = parseTape(bytes, name);
        state.fileName = name;
        state.file = file;
        state.spent = false;
        state.plays = 0;
        state.tweaksAfterPlay = 0;
        rebuildPulses();
        $('.zxt-workspace').classList.add('active');
        updateSummary();
        renderBlocks();
        drawScreen(findScreen(state.tape.blocks));
        updateProgress(null);
        updateEstimate();
        state.lastMarked = -1;
        renderMark();     // «WAV будет ~N МБ» — строка пустовала до первой правки настроек
        stage('tape_loaded');
        checkAudioSupport();
        funnel('tape_loaded', {
            format: state.tape.format,
            blocks: state.tape.blocks.length,
            seconds: Math.round(state.seconds),
            turbo: state.tape.blocks.some(b => b.id === 0x11 || b.id === 0x14) ? 1 : 0,
        });
        if (state.warnings && state.warnings.length) {
            window.__toast(T('warn_long_tape'), 'warning');
        }
    } catch (err) {
        /* ⚠️ Сообщения разборщика — служебные и английские («broken TAP
           chain»). Человеку на русской странице они говорят ровно ничего;
           это тот самый сырой JS-текст, который мы вычищаем по всему сайту.
           Переводим по КОДУ, а не по тексту. */
        const code = (err && err.code) || '';
        const key = { tap_chain: 'err_tap_chain', not_tzx: 'err_unknown_format', unknown_format: 'err_unknown_format' }[code];
        const msg = key ? T(key) : null;
        if (msg) { window.__toast(msg, 'error'); }
        else window.__handleError(err, 'zx-tape:open');
        funnel('open_failed', { code: code || (err && err.name) });
    }
}

/** Мини-распаковщик ZIP: у архивов World of Spectrum один файл внутри. */
async function unzipFirstTape(buf) {
    const dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
    let i = 0;
    while (i + 30 <= buf.length) {
        if (dv.getUint32(i, true) !== 0x04034b50) break;
        const method = dv.getUint16(i + 8, true);
        const compSize = dv.getUint32(i + 18, true);
        const nameLen = dv.getUint16(i + 26, true);
        const extraLen = dv.getUint16(i + 28, true);
        const name = new TextDecoder().decode(buf.subarray(i + 30, i + 30 + nameLen));
        const dataAt = i + 30 + nameLen + extraLen;
        if (/\.(tap|tzx|cdt)$/i.test(name) && compSize > 0) {
            const raw = buf.subarray(dataAt, dataAt + compSize);
            if (method === 0) return { bytes: raw, name };
            if (method === 8 && typeof DecompressionStream === 'function') {
                const ds = new DecompressionStream('deflate-raw');
                const out = new Uint8Array(await new Response(new Blob([raw]).stream().pipeThrough(ds)).arrayBuffer());
                return { bytes: out, name };
            }
        }
        i = dataAt + compSize;
    }
    return null;
}

/* ───────────────────────── настройка ───────────────────────── */

function bindSettings() {
    const bind = (sel, key, cast) => {
        const el = $(sel);
        if (!el) return;
        window.__toolSettings && window.__toolSettings.bind('zx-tape', el, key, state.settings[key]);
        const apply = () => {
            const v = el.type === 'checkbox' ? el.checked : (cast ? cast(el.value) : el.value);
            state.settings[key] = v;
            if (key === 'machine' && state.tape) { rebuildPulses(); renderBlocks(); updateSummary(); }
            if (state.settingsReady) trackStruggle('setting', key);
            if (state.player) state.player.set({ amplitude: state.settings.amplitude, invert: state.settings.invert });
            updateEstimate();
        };
        el.addEventListener('change', apply);
        el.addEventListener('input', apply);
        apply();
    };
    bind('#zxtRate', 'rate', Number);
    bind('#zxtBits', 'bits', Number);
    bind('#zxtAmp', 'amplitude', Number);
    bind('#zxtInvert', 'invert');
    bind('#zxtMachine', 'machine');
    bind('#zxtStopMarks', 'stopAtMarks');
    /* Флаг ставится ПОСЛЕ первичной привязки: восстановление сохранённых
       настроек — не правка руками, и считать её борьбой нельзя. */
    state.settingsReady = true;
}

function updateEstimate() {
    const el = $('.zxt-estimate');
    if (!el || !state.pulses) return;
    const n = sampleCount(state.pulses, clockOf(), state.settings.rate, state.settings.leadInMs, 0);
    const mb = (n * (state.settings.bits === 16 ? 2 : 1) + 44) / 1048576;
    el.textContent = T('wav_estimate', { size: mb.toFixed(1), time: fmtTime(state.seconds) });
}

/** Разовое списание за свой результат — для вкладок оцифровки и сборки. */
function spendKeyed(keys) {
    if (!window.__toolLimit || !window.__toolLimit.begin) return true;
    const run = window.__toolLimit.begin('zx-tape', keys || null);
    if (!run) { funnel('cap_wall', {}); return false; }
    run.commit();
    return true;
}

/* ───────────────────────── старт ───────────────────────── */

function initTapeTab() {
    const drop = $('#zxtDrop');
    if (drop) {
        /* Общий примитив, а не свой addEventListener: на медленной сети файл
           бывает выбран РАНЬШЕ, чем отработает модуль, и своё событие уходит
           в никуда — тул выглядит мёртвым до повторного выбора. */
        if (window.__bindUploaderFile) window.__bindUploaderFile(drop, (f) => onFile(f));
        else drop.addEventListener('tool-uploader:file', (e) => onFile(e.detail.file));
        drop.addEventListener('tool-uploader:clear', () => {
            $('.zxt-workspace').classList.remove('active');
            clearInterval(state.flashTimer);
            if (state.player) state.player.pause();
            state.tape = null;
        });
    }
    $('.zxt-blocks').addEventListener('click', (e) => {
        const btn = e.target.closest('[data-play]');
        if (btn) playFrom(+btn.dataset.play);
    });
    $('.zxt-play').addEventListener('click', async () => {
        const p = await ensurePlayer();
        if (p.state === 'playing') { p.pause(); return; }
        /* Возобновляем только ТУ ЖЕ ленту. Новая (или смена машины) — всегда
           с начала и с перезагрузкой импульсов. */
        if (!state.needsLoad && p.state !== 'idle' && p.state !== 'ended') {
            p.play(undefined, nextStopIndex(-1));
            return;
        }
        playFrom(0);
    });
    $('.zxt-rewind').addEventListener('click', () => playFrom(0));
    $('.zxt-test-tone').addEventListener('click', async () => {
        if (!checkAudioSupport()) return;
        const p = await ensurePlayer();
        p.testTone(4, { amplitude: state.settings.amplitude, invert: state.settings.invert });
        window.__toast(T('test_tone_hint'), 'info');
        funnel('test_tone', {});
    });
    $('.zxt-dl-wav').addEventListener('click', downloadWav);
    $('.zxt-dl-tap').addEventListener('click', () => downloadTape('tap'));
    $('.zxt-dl-tzx').addEventListener('click', () => downloadTape('tzx'));
    $('.zxt-extract-btn').addEventListener('click', extractFiles);
    $('.zxt-listing-btn').addEventListener('click', showListing);
    bindSettings();
}

/**
 * Браузер без Web Audio: синтезировать сигнал нечем.
 *
 * ⚠️ Тост здесь недостаточен — он исчезает, а человек остаётся перед кнопкой
 * «играть», которая молча ничего не делает. Пишем прямо в карточке
 * проигрывателя и уводим на рабочий путь: скачать WAV и сыграть его чем
 * угодно. Проверено на сборке WebKit, где Web Audio нет вовсе.
 */
function checkAudioSupport() {
    if (TapePlayer.audioAvailable()) return true;
    const card = $('.zxt-player');
    if (card && !card.querySelector('.zxt-noaudio')) {
        const note = document.createElement('div');
        note.className = 'zxt-noaudio';
        note.textContent = T('no_audio_notice');
        card.insertBefore(note, card.firstChild);
    }
    $$('.zxt-play, .zxt-rewind, .zxt-test-tone').forEach(b => {
        b.disabled = true;
        b.title = T('no_audio_notice');
    });
    funnel('no_audio', {});
    return false;
}

function initTabs() {
    $$('.zxt-tab').forEach(tab => {
        tab.addEventListener('click', () => {
            const name = tab.dataset.tab;
            $$('.zxt-tab').forEach(t => t.classList.toggle('zxt-tab-on', t === tab));
            $$('.zxt-pane').forEach(p => { p.style.display = p.dataset.pane === name ? '' : 'none'; });
            funnel('tab', { tab: name });
            if (state.player && state.player.state === 'playing') state.player.pause();
        });
    });
}

function boot() {
    /* Каждая вкладка поднимается ОТДЕЛЬНО: падение одной не должно оставлять
       две другие мёртвыми (так и случилось на первом же прогоне — исключение
       в настройках первой вкладки съело инициализацию оцифровки, и вкладка
       просто молчала в ответ на файл). */
    const guard = (name, fn) => {
        try { fn(); } catch (e) { window.__handleError && window.__handleError(e, 'zx-tape:init:' + name); }
    };
    guard('tabs', initTabs);
    guard('play', initTapeTab);
    guard('digitize', () => initDecodeTab({ T, playTape: loadDecodedTape, spend: spendKeyed }));
    guard('make', () => initMakeTab({ T, playTape: loadDecodedTape, spend: spendKeyed }));
    stage('ready');
}

/** Ленту, собранную другой вкладкой, показать в первой — она умеет играть. */
export function loadDecodedTape(blocks, name) {
    if (state.player && state.player.state === 'playing') state.player.pause();
    state.tape = { format: 'tzx', version: { major: 1, minor: 20 }, blocks };
    state.fileName = name || 'tape.tzx';
    /* Лента пришла из соседней вкладки — слот там уже списан, второй раз за ту
       же работу не берём. */
    state.file = null;
    state.spent = true;
    rebuildPulses();
    $$('.zxt-tab').forEach(t => t.classList.toggle('zxt-tab-on', t.dataset.tab === 'play'));
    $$('.zxt-pane').forEach(p => { p.style.display = p.dataset.pane === 'play' ? '' : 'none'; });
    $('.zxt-workspace').classList.add('active');
    updateSummary();
    renderBlocks();
    drawScreen(findScreen(blocks));
    updateEstimate();
    window.scrollTo && null;      // намеренно без прокрутки: страница не прыгает
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
